import type {
  Lead_Agents,
  Leads,
  Lots,
  Portals,
  Teams,
  Teams_Portals,
  Teams_Users,
} from '@graphql/__generated__/graphql';
import type { Me } from '@modules/_shared/providers/AppDataProvider';
import { isViewerOrLedUser } from '@modules/_shared/utils/isViewerOrLedUser';

type TUser = Pick<
  NonNullable<Me>,
  'id' | 'is_admin' | 'tenant_id' | 'user_ids_in_led_teams' | 'is_broker'
>;

type LeadAssignment = Pick<Lead_Agents, 'claimed_at' | 'user_id'> & {
  passed_at?: Lead_Agents['passed_at'];
};

export const userHasClaimedLeadAssignment = (
  user: TUser | null | undefined,
  leadAgents: readonly LeadAssignment[],
  { excludePassed = false }: { excludePassed?: boolean } = {},
): boolean =>
  leadAgents.some(
    agent =>
      agent.claimed_at != null &&
      (!excludePassed || agent.passed_at == null) &&
      isViewerOrLedUser(user?.id, user?.user_ids_in_led_teams, agent.user_id),
  );

export const userCanViewLeadDetails = (
  user: TUser | null | undefined,
  lead: Pick<Leads, 'created_by' | 'origin_enquiry_id'> & {
    lead_agents: Pick<Lead_Agents, 'claimed_at' | 'user_id'>[];
  },
) => {
  return (
    user?.is_admin ||
    // The creator shortcut is for ordinary leads. Profile leads stay
    // anonymized for agents until their assignment is claimed.
    (lead.origin_enquiry_id == null && user?.id === lead.created_by) ||
    // show leads if there is a claimed lead agent for me or someone in my team (as team leader) or if it is a result page lead
    userHasClaimedLeadAssignment(user, lead.lead_agents)
  );
};

export const userCanManageLead = (
  user: TUser | null | undefined,
  lead:
    | (Pick<Leads, 'created_by' | 'origin_enquiry_id'> & {
        lead_agents: Pick<
          Lead_Agents,
          'claimed_at' | 'user_id' | 'passed_at'
        >[];
      })
    | null
    | undefined,
): boolean => {
  if (user == null || lead == null) {
    return false;
  }

  return (
    user.is_admin === true ||
    (lead.origin_enquiry_id == null && user.id === lead.created_by) ||
    (lead.origin_enquiry_id != null &&
      userHasClaimedLeadAssignment(user, lead.lead_agents, {
        excludePassed: true,
      }))
  );
};

type Portal = Pick<Portals, 'team_id'> & {
  teams_portals: Pick<Teams_Portals, 'team_id'>[];
};

export type LightListing = Pick<Lots, 'broker_id'> & {
  broker?: {
    active_teams_users?: ReadonlyArray<
      Pick<Teams_Users, 'team_id'> & {
        team?: Pick<Teams, 'lots_member_access'> | null;
      }
    > | null;
  } | null;
};

/**
 * Mirrors the Hasura `lots_access_view.can_edit` rule client-side so the UI
 * can render in read-only mode even though the server would also reject
 * unauthorised mutations.
 */
export const canEditListing = (
  me: Me | null | undefined,
  listing: LightListing | null | undefined,
): boolean => {
  if (listing == null || me == null) {
    return false;
  }

  if (me.is_admin || listing.broker_id == null || listing.broker_id === me.id) {
    return true;
  }

  const myTeams = me.teams ?? [];
  const brokerTeams = listing.broker?.active_teams_users ?? [];
  for (const myMembership of myTeams) {
    const brokerMembership = brokerTeams.find(
      bt => bt.team_id === myMembership.team_id,
    );

    if (brokerMembership == null) {
      continue;
    }

    if (myMembership.is_leader === true) {
      return true;
    }

    // If one of the teams the broker is a member of has edit_all access, they can edit the listing
    if (brokerMembership.team?.lots_member_access === 'edit_all') {
      return true;
    }
  }
  return false;
};

type MlsListing = Pick<Lots, 'broker_id'> & {
  broker?: {
    id: string;
    teams_users?: ReadonlyArray<Pick<Teams_Users, 'team_id'>> | null;
  } | null;
};

export const canUpdateMls = (
  me: Me | null | undefined,
  listing: MlsListing | null | undefined,
): boolean => {
  if (me == null || listing == null) {
    return false;
  }
  if (me.is_admin) {
    return true;
  }
  if (listing.broker?.id === me.id) {
    return true;
  }
  const ledTeamIds = new Set(
    me.teams?.filter(t => t.is_leader).map(t => t.team_id) ?? [],
  );
  return (
    listing.broker?.teams_users?.some(tu => ledTeamIds.has(tu.team_id)) ?? false
  );
};

export const userCanPublishPortalListing = (
  user: Pick<NonNullable<Me>, 'teams_users'> | null | undefined,
  portal: Portal,
  isLive: boolean,
) => {
  const allowedTeamsIds = [
    portal.team_id,
    ...portal.teams_portals.map(({ team_id }) => team_id),
  ].filter(id => id != null);
  const userTeamIds = user?.teams_users.map(({ team_id }) => team_id) ?? [];

  // If the listing is live, we need to be able to unpublish it.
  return isLive || userTeamIds.some(id => allowedTeamsIds.includes(id));
};
